import { NavLink, Outlet } from 'react-router-dom';
import {
  BadgeCheck,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CalendarDays,
  Clock,
  Globe2,
  HardDriveDownload,
  KeyRound,
  LayoutGrid,
  Mail,
  Network,
  Palette,
  ShieldCheck,
  Sliders,
  SwatchBook,
  UsersRound,
  Wallet,
  Webhook,
  Workflow,
} from 'lucide-react';
import { usePermission } from '@/hooks/usePermission';

interface Tab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  permission?: string;
}

const tabs: Tab[] = [
  { to: '/organization', label: 'Overview', icon: LayoutGrid, end: true },
  { to: '/organization/profile', label: 'Company Profile', icon: Building2, permission: 'organizations.view' },
  { to: '/organization/branches', label: 'Branches', icon: Network, permission: 'organizations.view' },
  { to: '/organization/departments', label: 'Departments', icon: Network, permission: 'organizations.view' },
  { to: '/organization/designations', label: 'Designations', icon: BadgeCheck, permission: 'organizations.view' },
  { to: '/organization/members', label: 'Members', icon: UsersRound, permission: 'users.view' },
  { to: '/organization/workspace-settings', label: 'Workspace', icon: Sliders, permission: 'settings.view' },
  { to: '/organization/localization', label: 'Localization', icon: Globe2, permission: 'settings.view' },
  { to: '/organization/smtp', label: 'SMTP', icon: Mail, permission: 'settings.manage' },
  { to: '/organization/branding', label: 'Branding', icon: Palette, permission: 'settings.view' },
  { to: '/organization/theme', label: 'Themes', icon: SwatchBook, permission: 'settings.view' },
  { to: '/organization/ai-settings', label: 'AI Activity', icon: Bot, permission: 'ai.view' },
  { to: '/organization/reports', label: 'Reports', icon: BarChart3, permission: 'reports.view' },
  { to: '/organization/notifications', label: 'Notifications', icon: Bell, permission: 'notifications.manage' },
  { to: '/organization/holidays', label: 'Holidays', icon: CalendarDays, permission: 'calendar.manage' },
  { to: '/organization/time-tracking', label: 'Time Tracking', icon: Clock, permission: 'time_tracking.manage' },
  { to: '/organization/billing', label: 'Billing', icon: Wallet, permission: 'billing.view' },
  { to: '/organization/api-keys', label: 'API Keys', icon: KeyRound, permission: 'settings.manage' },
  { to: '/organization/webhooks', label: 'Webhooks', icon: Webhook, permission: 'settings.manage' },
  { to: '/organization/workflow', label: 'Workflow', icon: Workflow, permission: 'settings.manage' },
  { to: '/organization/security', label: 'Security', icon: ShieldCheck, permission: 'settings.view' },
  { to: '/organization/backup', label: 'Backup', icon: HardDriveDownload, permission: 'settings.manage' },
];

export function OrganizationLayout() {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Organization</h1>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          Manage your company profile, structure, members, and workspace settings.
        </p>
      </div>

      <div className="-mb-px flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
        {tabs.map((tab) => (
          <TabLink key={tab.to} tab={tab} />
        ))}
      </div>

      <Outlet />
    </div>
  );
}

function TabLink({ tab }: { tab: Tab }) {
  const allowed = usePermission(tab.permission ?? '__always__') || !tab.permission;
  if (!allowed) {
    return null;
  }

  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      className={({ isActive }) =>
        `flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
        }`
      }
    >
      <tab.icon className="h-4 w-4" />
      {tab.label}
    </NavLink>
  );
}
